import { useMemo, useEffect, useCallback, useRef } from "react";
import { useFileExplorerUi } from "../../store/fileExplorerUi.store";
import { useFileExplorerSearchParams } from "./useFileExplorerSearchParams";
import type { FileItem } from "../../types";

interface UseFileSelectionOptions {
  allFiles: FileItem[];
}

export function useFileSelection({ allFiles }: UseFileSelectionOptions) {
  const { selectedFile, setSelectedFile } = useFileExplorerUi();
  const { search, updateSearchParams } = useFileExplorerSearchParams();
  const isClosingRef = useRef(false);

  const urlFileId = search.fileId;
  const urlSelectedFile = useMemo(() => {
    if (!urlFileId) return null;
    return allFiles.find((f) => f.id === urlFileId) || null;
  }, [urlFileId, allFiles]);

  const validSelectedFile = useMemo(() => {
    if (urlSelectedFile) return urlSelectedFile;
    if (!selectedFile) return null;
    return allFiles.find((f) => f.id === selectedFile.id) || null;
  }, [urlSelectedFile, selectedFile, allFiles]);

  useEffect(() => {
    if (isClosingRef.current) {
      isClosingRef.current = false;
      return;
    }

    if (urlFileId && validSelectedFile?.id === urlFileId) {
      return;
    }

    if (validSelectedFile && validSelectedFile.id !== urlFileId) {
      updateSearchParams({ fileId: validSelectedFile.id });
      return;
    }
  }, [validSelectedFile, urlFileId, updateSearchParams]);

  useEffect(() => {
    if (urlSelectedFile && urlSelectedFile.id !== selectedFile?.id) {
      setSelectedFile(urlSelectedFile);
    } else if (!urlSelectedFile && selectedFile) {
      if (!urlFileId) {
        setSelectedFile(null);
      }
    }
  }, [urlSelectedFile, urlFileId, selectedFile, setSelectedFile]);

  const handleFileSelect = useCallback(
    (file: FileItem) => {
      setSelectedFile(file);
      updateSearchParams({ fileId: file.id });
    },
    [setSelectedFile, updateSearchParams]
  );

  const handleCloseDetails = useCallback(() => {
    isClosingRef.current = true;
    setSelectedFile(null);
    updateSearchParams({ fileId: undefined, "file-view": undefined });
  }, [setSelectedFile, updateSearchParams]);

  const handleFileNavigate = useCallback(
    (direction: "prev" | "next") => {
      if (!validSelectedFile) return;

      const currentIndex = allFiles.findIndex(
        (f) => f.id === validSelectedFile.id
      );
      if (direction === "prev" && currentIndex > 0) {
        const prevFile = allFiles[currentIndex - 1];
        updateSearchParams({ fileId: prevFile.id });
        setSelectedFile(prevFile);
      } else if (direction === "next" && currentIndex < allFiles.length - 1) {
        const nextFile = allFiles[currentIndex + 1];
        updateSearchParams({ fileId: nextFile.id });
        setSelectedFile(nextFile);
      }
    },
    [validSelectedFile, allFiles, setSelectedFile, updateSearchParams]
  );

  return {
    validSelectedFile,
    handleFileSelect,
    handleCloseDetails,
    handleFileNavigate,
  };
}
